import React from 'react';
import theme from '../../theme';
import styles from './Input.module.css';

const { spacingScale, borderRadius } = theme;

export default function Input({
  label,
  placeholder,
  value,
  onChange,
  type = 'text',
  role = 'admin',
  className = '',
  style = {},
  error,
  helperText,
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  ...props
}) {
  const palette = theme.rolePalettes[role] || theme.rolePalettes.admin;
  const tokens = theme.componentTokens.input;

  const inputStyles = {
    borderRadius: theme.borderRadius.lg, // 12px (rounded-xl)
    border: error 
      ? `1px solid ${palette.error.main}` 
      : `1px solid ${theme.baseColors.brand[900]}0D`,
    padding: `${spacingScale[2]} ${spacingScale[4]}`, // 8px 16px, but design uses py-3.5 (14px)
    paddingTop: startIcon ? '14px' : '14px',
    paddingBottom: startIcon ? '14px' : '14px',
    paddingLeft: startIcon ? '44px' : '16px', // pl-11 = 44px
    paddingRight: endIcon ? '44px' : '16px',
    fontSize: '0.875rem', // text-sm
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500, // font-medium
    width: fullWidth ? '100%' : 'auto',
    background: disabled 
      ? theme.baseColors.grey[100] 
      : '#f2f5f4', // bg-[#f2f5f4] from design system
    color: '#0f291e', // text-[#0f291e]
    transition: `all ${theme.motion.duration.normal} ${theme.motion.easing.easeInOut}`,
    outline: 'none',
    ...style,
  };

  const focusStyles = {
    borderColor: error 
      ? palette.error.main 
      : `${theme.baseColors.brand[950]}33`,
    backgroundColor: theme.baseColors.white, // focus:bg-white
    boxShadow: error
      ? `0 0 0 2px ${palette.error.light}40`
      : `0 0 0 2px ${theme.baseColors.brand[950]}1A`,
  };

  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing,
    width: fullWidth ? '100%' : 'auto',
  };

  const labelStyles = {
    fontSize: '0.625rem', // text-[10px]
    fontWeight: 800, // font-extrabold
    color: '#2d4a3e', // text-[#2d4a3e]
    textTransform: 'uppercase',
    letterSpacing: '0.2em', // tracking-[0.2em]
    marginBottom: spacingScale[1], // space-y-1.5 = 6px
    marginLeft: spacingScale[1], // ml-1 = 4px
    display: 'block',
  };

  const helperTextStyles = {
    fontSize: theme.typography.caption.fontSize,
    color: error ? palette.error.main : theme.baseColors.grey[600],
    marginTop: theme.spacing * 0.5,
  };

  const inputContainerStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const iconStyles = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    fontSize: '20px',
    transition: `color ${theme.motion.duration.normal} ${theme.motion.easing.easeInOut}`,
  };

  const startIconStyles = {
    ...iconStyles,
    left: spacingScale[4], // left-4 = 16px
    color: '#0f291e66', // text-[#0f291e]/40
  };

  const endIconStyles = {
    ...iconStyles,
    right: spacingScale[4],
    color: error ? palette.error.main : '#0f291e66',
  };

  const inputWithIconsStyles = {
    ...inputStyles,
  };

  return (
    <div className={`${styles.inputContainer} ${className}`} style={containerStyles}>
      {label && (
        <label style={labelStyles}>
          {label}
        </label>
      )}
      <div style={inputContainerStyles} className={styles.inputGroup}>
        {startIcon && React.isValidElement(startIcon) && (
          <span style={startIconStyles} className={styles.startIcon}>
            {startIcon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          style={inputWithIconsStyles}
          className={styles.input}
          data-error={error ? 'true' : 'false'}
          {...props}
        />
        {endIcon && React.isValidElement(endIcon) && (
          <span style={endIconStyles} className={styles.endIcon}>
            {endIcon}
          </span>
        )}
      </div>
      {helperText && (
        <div style={helperTextStyles}>
          {helperText}
        </div>
      )}
    </div>
  );
} 